import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { Skill } from '../types';

import { ImageAskTool } from 'js/src/agent/tools/ImageAskTool';
import { SetMediaTool } from 'js/src/agent/tools/SetMediaTool';

export const cropMediaSkill: Skill = {
    name: SKILL_NAMES.cropMedia,
    description:
        'Load BEFORE adjusting how media is framed (subject cut off, bad crop, needs zoom or motion, media too bright under text).',
    kind: 'howto',
    surfaces: ['sketch'],
    get instructions(): string {
        return `# Cropping and Reframing Media

A media asset's frame is fixed by the design — you reframe the media *within* it
using \`${SetMediaTool.toolName}\` (scale, position, Ken Burns, brightness).

## How positioning works
- Position translates the media relative to the center of its frame, bounded by the
  asset's \`mediaXTranslationRange\` and \`mediaYTranslationRange\` in the video state.
  Read the ranges first — a position outside them does nothing.
- **The axes are inverted from what you'd expect**: to move the media LEFT pass a
  positive x, RIGHT a negative x; UP a positive y, DOWN a negative y. (Think of it
  as moving the frame's window over the media, not the media itself.) So
  \`{ x: 10 }\` shifts it left, \`{ x: -10 }\` right, \`{ y: 15 }\` up, \`{ y: -15 }\` down.
- \`scale\` is a percentage: above 100 enlarges the media (width and height), below
  100 shrinks it. Scaling up zooms in and widens the translation ranges — if the
  range is too narrow to reach the crop you want, increase the scale first.

## The reframe loop
1. Read the asset's current scale, translation, and ranges from the video state.
2. Apply \`${SetMediaTool.toolName}\` with the new scale/position.
3. Re-render the scene (\`${ImageAskTool.toolName}\`, optionally passing the media id too so the
   model can reason about how the media sits in its frame) and check the result.
4. Iterate until the composition lands.

## What good framing means
- The subject is well-composed: no cut-off faces or key objects, especially at the
  frame edges.
- Nothing important sits under text, captions, or overlaid assets — reposition the
  media (or dim it with \`brightness\` when it's a backdrop for text) rather than
  accepting a collision.
- **Ken Burns** (\`zoom_in\`, \`zoom_out\`, \`pan_*\`) adds motion to still images —
  useful when a photo has to carry a scene; \`none\` disables it.`;
    },
};
